import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import _ from "lodash"
import * as ServiceAPI from "@services"
import * as constantsAPI from "@constants"
import { userKeycloakAttributes } from "../reduxSagas/admin/actions"
import UserService from "../services/UserService"

const { APIEndPoint: { API_END_POINTS = {} } = {} } = constantsAPI || {}

/** Visible tabs only. Hidden tabs refresh when shown, or when another tab already saw a change. */
const PERMISSION_REFRESH_MS = 15000000
const PERMISSION_SYNC_KEY = "dg-role-permission-revision"

/**
 * Same mapping as login: "Y" is granted, "N" is denied, "D" stays denied and locked.
 */
export const formatRoleAttributes = (data = {}) =>
  _.reduce(
    data,
    (acc, val, key) => {
      const keyArr = String(key).split("___")
      const flag = Array.isArray(val) ? val[0] : val
      _.set(acc, _.join(keyArr, "."), flag === "D" ? "D" : flag === "Y")
      return acc
    },
    {}
  )

const readSessionBody = () => {
  const isOidc = localStorage.getItem("is_oidc") === "true"
  return {
    body: {
      tenantName: localStorage.getItem("tenantName") || "",
      realmId: localStorage.getItem("X-KC-Realm-ID"),
      keycloackRoleName: localStorage.getItem("sourceRole") || "",
      keycloakUserId: isOidc ? "" : localStorage.getItem("userId") || "",
    },
    headerKit: isOidc ? { headers: { is_oidc: true } } : {},
  }
}

export const usePermissionSync = () => {
  const dispatch = useDispatch()
  const currentAttributes = useSelector(
    (state) => state.adminReducer.userKeycloakAttributes
  )
  const currentRef = useRef(currentAttributes)
  currentRef.current = currentAttributes
  const inFlight = useRef(false)

  useEffect(() => {
    let stopped = false

    const pull = async () => {

      return

      if (stopped || inFlight.current) return
      if (!UserService.isLoggedIn()) return
      if (!localStorage.getItem("sourceRole")) return
      if (_.isEmpty(currentRef.current)) return

      inFlight.current = true
      try {
        const { body, headerKit } = readSessionBody()
        const res = await ServiceAPI.fetchCommonRoleData(
          API_END_POINTS.getUserDetail,
          body,
          headerKit
        )
        const raw = _.get(res, "data.data.role.attributes")
        if (!_.isPlainObject(raw) || _.isEmpty(raw)) return
        const next = formatRoleAttributes(raw)
        if (stopped || _.isEqual(next, currentRef.current)) return
        dispatch(userKeycloakAttributes(next))
        try {
          localStorage.setItem(PERMISSION_SYNC_KEY, String(Date.now()))
        } catch (storageError) {
          // Another tab can still refresh on its own interval.
        }
      } catch (error) {
        // Keep the last known permissions when the check cannot complete.
      } finally {
        inFlight.current = false
      }
    }

    const pullIfVisible = () => {
      if (document.visibilityState !== "visible") return
      pull()
    }

    const onStorage = (event) => {
      if (event.key === PERMISSION_SYNC_KEY && event.newValue) pull()
    }

    const timer = setInterval(pullIfVisible, PERMISSION_REFRESH_MS)
    document.addEventListener("visibilitychange", pullIfVisible)
    window.addEventListener("storage", onStorage)

    return () => {
      stopped = true
      clearInterval(timer)
      document.removeEventListener("visibilitychange", pullIfVisible)
      window.removeEventListener("storage", onStorage)
    }
  }, [dispatch])
}
